import { useState } from 'react';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import type { CaptureStaffResponse, CaptureStaffRow, VpageChatDailyRow } from '../types';
import { asNumber, num, uniq } from '../utils';
import { RANGE_OPTIONS } from '../ranges';

interface StaffScreenProps {
  data: CaptureStaffResponse | null;
  loaded: boolean;
  busy: boolean;
  store: string;
  setStore: (store: string) => void;
  range: string;
  onRange: (range: string) => void;
}


export function StaffScreen({ data, loaded, busy, store, setStore, range, onRange }: StaffScreenProps) {
  const [selected, setSelected] = useState<string | null>(null);
  if (busy && !loaded) return <div className="card empty">Đang tải hiệu suất nhân viên...</div>;
  if (!loaded) return <div className="card empty">Mở module Theo nhân viên để tải dữ liệu.</div>;
  const all = data?.staff || [];
  const chatAll = data?.chat || [];
  const daily = data?.chatDaily || [];
  const outcomeDaily = data?.outcomeDaily || [];
  const stores = uniq([...all.map(row => row.store_code || ''), ...chatAll.map(row => row.store_code || '')].filter(Boolean));
  const rows = all.filter(row => !store || row.store_code === store);
  const chatRows = chatAll.filter(row => !store || row.store_code === store);
  const sumOf = (k: keyof CaptureStaffRow) => rows.reduce((s, r) => s + asNumber(r[k] as number | string | undefined), 0);
  const agg = {
    bao_cao: sumOf('bao_cao'),
    mua_hang: sumOf('mua_hang'),
    chot: sumOf('chot'),
    chua_chot: sumOf('chua_chot_co_sdt') + sumOf('chua_chot_khong_sdt'),
    service: sumOf('service'),
  };
  const crAll = agg.mua_hang ? Math.round((1000 * agg.chot) / agg.mua_hang) / 10 : 0;
  const STORE_ORDER = ['NCV', 'HHT', 'KVC', 'TBT', 'TH', 'KHAC'];
  const dailyStores = STORE_ORDER.filter(s => daily.some(d => d.store_code === s));
  const dayMap = new Map<string, Record<string, VpageChatDailyRow>>();
  daily.forEach(d => { const k = d.ngay || ''; if (!dayMap.has(k)) dayMap.set(k, {}); dayMap.get(k)![d.store_code || '?'] = d; });
  const days = [...dayMap.keys()].sort().reverse();
  const dailyForStore = daily.filter(d => d.store_code === store).sort((a, b) => (b.ngay || '').localeCompare(a.ngay || ''));
  const rateTone = (pct: number): 'green' | 'amber' | 'red' => (pct >= 50 ? 'green' : pct >= 30 ? 'amber' : 'red');

  const selectedRow = selected ? rows.find(r => (r.staff_id || '') === selected) : null;
  const selectedDays = selected
    ? outcomeDaily.filter(o => (o.staff_id || '') === selected).sort((a, b) => (b.ngay || '').localeCompare(a.ngay || ''))
    : [];
  const rangeLabel = RANGE_OPTIONS.find(o => o.v === range)?.l || 'Tất cả';

  return (
    <>
      <section className="grid kpis">
        <KpiCard label="Tổng tiếp" value={num(agg.bao_cao)} sub={store ? `store ${store}` : `${rows.length} nhân viên`} />
        <KpiCard label="Mua hàng" value={num(agg.mua_hang)} sub="need_type = sales" tone="blue" />
        <KpiCard label="Đã chốt" value={num(agg.chot)} sub="đơn thành công" tone="green" />
        <KpiCard label="Chưa chốt" value={num(agg.chua_chot)} sub="đơn miss (có/không SĐT)" tone="amber" />
        <KpiCard label="Service" value={num(agg.service)} sub="bảo hành / dịch vụ" tone="violet" />
        <KpiCard label="CR đơn" value={`${crAll}%`} sub="chốt / mua hàng" tone={rateTone(crAll)} />
      </section>

      <section className="filters">
        <div className="actions start">
          <span className="kpi-label">Store</span>
          <button className={`tab ${!store ? 'active' : ''}`} onClick={() => { setStore(''); setSelected(null); }}>Tất cả</button>
          {stores.map(s => <button key={s} className={`tab ${store === s ? 'active' : ''}`} onClick={() => { setStore(s); setSelected(null); }}>{s}</button>)}
        </div>
        <div className="actions start">
          <span className="kpi-label">Thời gian</span>
          {RANGE_OPTIONS.map(opt => (
            <button key={opt.v} className={`tab ${range === opt.v ? 'active' : ''}`} onClick={() => onRange(opt.v)}>{opt.l}</button>
          ))}
        </div>
      </section>

      <section className="card">
        <div className="section-title">
          <h2>Hiệu suất theo nhân viên · đơn hàng &amp; service</h2>
          <span>{num(rows.length)} nhân viên · CR = chốt / mua hàng (loại Service + không phân loại) · bấm 1 dòng để xem breakdown theo ngày</span>
        </div>
        <div className="scroll">
          <table>
            <thead><tr>
              <th>Store</th><th>Nhân viên</th><th>Tổng tiếp</th><th>Mua hàng</th><th>Chốt</th>
              <th>Chưa chốt +SĐT</th><th>Chưa chốt −SĐT</th><th>Service</th><th>Không PL</th><th>CR%</th>
            </tr></thead>
            <tbody>{rows.map(row => {
              const cr = asNumber(row.cr_pct);
              const sid = row.staff_id || '';
              const active = selected === sid;
              return (
                <tr
                  key={sid + '|' + (row.store_code || '')}
                  className={active ? 'click-row active' : 'click-row'}
                  onClick={() => setSelected(active ? null : sid)}
                >
                  <td><b>{row.store_code || '-'}</b></td>
                  <td>{row.staff_name || <span className="muted">(chưa map)</span>}</td>
                  <td className="tnum"><b>{num(asNumber(row.bao_cao))}</b></td>
                  <td className="tnum">{num(asNumber(row.mua_hang))}</td>
                  <td className="tnum"><b>{num(asNumber(row.chot))}</b></td>
                  <td className="tnum">{num(asNumber(row.chua_chot_co_sdt))}</td>
                  <td className="tnum muted">{num(asNumber(row.chua_chot_khong_sdt))}</td>
                  <td className="tnum">{num(asNumber(row.service))}</td>
                  <td className="tnum muted">{num(asNumber(row.khong_phan_loai))}</td>
                  <td><Tag tone={rateTone(cr)}>{cr}%</Tag></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      </section>

      {selectedRow && (
        <section className="card" style={{ borderLeft: '4px solid var(--blue)' }}>
          <div className="section-title">
            <h2>Chi tiết nhân viên · {selectedRow.staff_name || '(chưa map)'} {selectedRow.store_code ? `· ${selectedRow.store_code}` : ''}</h2>
            <span>
              Khoảng: {rangeLabel} · {num(selectedDays.length)} ngày có báo cáo · CR = chốt / mua hàng (loại Service + không PL)
              <button className="tab" style={{ marginLeft: 8 }} onClick={() => setSelected(null)}>Đóng</button>
            </span>
          </div>
          <div className="grid kpis" style={{ marginBottom: 14 }}>
            <KpiCard label="Tổng tiếp" value={num(asNumber(selectedRow.bao_cao))} sub="lượt khách tiếp" />
            <KpiCard label="Mua hàng" value={num(asNumber(selectedRow.mua_hang))} sub="need_type = sales" tone="blue" />
            <KpiCard label="Đã chốt" value={num(asNumber(selectedRow.chot))} sub="đơn thành công" tone="green" />
            <KpiCard label="Chưa chốt +SĐT" value={num(asNumber(selectedRow.chua_chot_co_sdt))} sub="còn cứu được" tone="amber" />
            <KpiCard label="Chưa chốt −SĐT" value={num(asNumber(selectedRow.chua_chot_khong_sdt))} sub="không liên hệ được" />
            <KpiCard label="Service" value={num(asNumber(selectedRow.service))} sub="bảo hành / dịch vụ" tone="violet" />
            <KpiCard label="Không PL" value={num(asNumber(selectedRow.khong_phan_loai))} sub="thiếu phân loại" />
            <KpiCard label="CR đơn" value={`${asNumber(selectedRow.cr_pct)}%`} sub="chốt / mua hàng" tone={rateTone(asNumber(selectedRow.cr_pct))} />
          </div>
          <div className="kpi-label" style={{ marginBottom: 6 }}>Xu hướng theo ngày</div>
          <div className="scroll">
            <table>
              <thead><tr>
                <th>Ngày</th><th>Tổng tiếp</th><th>Mua hàng</th><th>Chốt</th>
                <th>Chưa chốt (có SĐT)</th><th>Chưa chốt (không SĐT)</th><th>Service</th><th>Không phân loại</th><th>CR%</th>
              </tr></thead>
              <tbody>{selectedDays.map(o => {
                const cr = asNumber(o.cr_pct);
                return (
                  <tr key={o.ngay}>
                    <td className="tnum muted">{o.ngay}</td>
                    <td className="tnum"><b>{num(asNumber(o.tong_tiep))}</b></td>
                    <td className="tnum">{num(asNumber(o.mua_hang))}</td>
                    <td className="tnum"><b>{num(asNumber(o.chot))}</b></td>
                    <td className="tnum">{num(asNumber(o.chua_chot_co_sdt))}</td>
                    <td className="tnum muted">{num(asNumber(o.chua_chot_khong_sdt))}</td>
                    <td className="tnum">{num(asNumber(o.service))}</td>
                    <td className="tnum muted">{num(asNumber(o.khong_phan_loai))}</td>
                    <td><Tag tone={rateTone(cr)}>{cr}%</Tag></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card">
        <div className="section-title">
          <h2>Chăm sóc VPage · tổng theo ngày {store ? `· ${store}` : '· từng showroom'}</h2>
          <span>14 ngày gần đây · tin shop gửi khách (tổng thật theo page) &amp; kết bạn mới</span>
        </div>
        <div className="scroll">
          {store ? (
            <table>
              <thead><tr><th>Ngày</th><th>Tin gửi</th><th>Kết bạn mới</th><th>Số sale chăm</th></tr></thead>
              <tbody>{dailyForStore.map(d => (
                <tr key={d.ngay}>
                  <td className="tnum muted">{d.ngay}</td>
                  <td className="tnum"><b>{num(asNumber(d.tin_shop_gui))}</b></td>
                  <td className="tnum">{num(asNumber(d.khach_moi))}</td>
                  <td className="tnum">{num(asNumber(d.so_sale))}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : (
            <table>
              <thead><tr><th>Ngày</th>{dailyStores.map(s => <th key={s}>{s}</th>)}<th>Tổng tin</th><th>Kết bạn mới</th></tr></thead>
              <tbody>{days.map(day => {
                const row = dayMap.get(day) || {};
                const totalTin = dailyStores.reduce((sum, s) => sum + asNumber(row[s]?.tin_shop_gui), 0);
                const totalKb = dailyStores.reduce((sum, s) => sum + asNumber(row[s]?.khach_moi), 0);
                return (
                  <tr key={day}>
                    <td className="tnum muted">{day}</td>
                    {dailyStores.map(s => <td key={s} className="tnum">{row[s] ? num(asNumber(row[s].tin_shop_gui)) : '-'}</td>)}
                    <td className="tnum"><b>{num(totalTin)}</b></td>
                    <td className="tnum">{num(totalKb)}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}
        </div>
      </section>

      <section className="card">
        <div className="section-title">
          <h2>Chăm sóc VPage theo sale · từng ngày</h2>
          <span>{num(chatRows.length)} dòng · tin nhắn gửi khách &amp; kết bạn theo nhân viên (nguồn chat VPage)</span>
        </div>
        <div className="scroll">
          <table>
            <thead><tr><th>Ngày</th><th>Store</th><th>Nhân viên</th><th>Tin gửi khách</th><th>Hội thoại</th><th>Kết bạn mới</th></tr></thead>
            <tbody>{chatRows.map((r, i) => (
              <tr key={(r.ngay || '') + '|' + (r.staff_name || '') + i}>
                <td className="tnum muted">{r.ngay}</td>
                <td><b>{r.store_code || '-'}</b></td>
                <td>{r.staff_name || <span className="muted">(chưa map)</span>}</td>
                <td className="tnum"><b>{num(asNumber(r.tin_shop_gui))}</b></td>
                <td className="tnum">{num(asNumber(r.so_hoi_thoai))}</td>
                <td className="tnum">{num(asNumber(r.khach_moi))}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      <div className="card empty">
        ⚠️ Lưu ý: CR chỉ tính trên đơn hàng (need_type=sales); Service/bảo hành &amp; "không phân loại" KHÔNG nằm trong mẫu số. Tên NV trên chat phủ ~55% tin (lịch sử thiếu); "kết bạn mới" = hội thoại mới trong ngày (proxy).
      </div>
    </>
  );
}
